import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminConstraintKeys } from "@/lib/query";

export interface AdminConstraintGroup {
  id: string;
  name: string;
  description: string | null;
}

export interface AdminConstraint {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  groupId: string;
  groupName: string;
  usage: {
    solutions: number;
    accounts: number;
    resourceRatings: number;
    total: number;
  };
}

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function useConstraintGroups() {
  return useQuery({
    queryKey: adminConstraintKeys.groups(),
    queryFn: async (): Promise<AdminConstraintGroup[]> => {
      const res = await fetch("/api/admin/constraint-groups");
      const data = await readJsonOrThrow<{ groups: AdminConstraintGroup[] }>(res);
      return data.groups;
    },
  });
}

export function useAdminConstraints() {
  return useQuery({
    queryKey: adminConstraintKeys.list(),
    queryFn: async (): Promise<AdminConstraint[]> => {
      const res = await fetch("/api/admin/constraints");
      const data = await readJsonOrThrow<{ constraints: AdminConstraint[] }>(res);
      return data.constraints;
    },
  });
}

export function useCreateConstraint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description: string | null;
      constraintGroupId: string;
      active: boolean;
    }) => {
      const res = await fetch("/api/admin/constraints", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      return readJsonOrThrow<AdminConstraint>(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminConstraintKeys.all });
      toast.success("Constraint created");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to create constraint");
    },
  });
}

export function useUpdateConstraint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      description?: string | null;
      constraintGroupId?: string;
      active?: boolean;
    }) => {
      const { id, ...patch } = input;
      const res = await fetch(`/api/admin/constraints/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      return readJsonOrThrow<AdminConstraint>(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminConstraintKeys.all });
      toast.success("Constraint updated");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update constraint");
    },
  });
}

export function useDeleteConstraint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/admin/constraints/${id}`, { method: "DELETE" });
      await readJsonOrThrow<{ ok: true }>(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminConstraintKeys.all });
      toast.success("Constraint deleted");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to delete constraint");
    },
  });
}
